import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "../pages/components/Navbar";
import Footer from "../pages/components/Footer";
import { API_BASE_URL } from "../config";
import { useAppContext } from "../context/AppContext";
import {
  DynamicDetailedProductCard,
  getItemKey,
  normalizeImage,
} from "./components/ProductCard";

const getSellingPrice = (item) => Number(item?.price || 0);
const getStrikePrice = (item) => Number(item?.mrp || 0);

const parseWeightToGrams = (weight) => {
  const value = String(weight || "")
    .toLowerCase()
    .trim();
  if (value.endsWith("kg"))
    return Number(value.replace("kg", "").trim()) * 1000;
  if (value.endsWith("g")) return Number(value.replace("g", "").trim());
  return 0;
};

const formatWeight = (grams) => {
  if (grams >= 1000) return `${grams / 1000}kg`;
  return `${grams}g`;
};

const prepareProduct = (product, categoryMap = {}) => {
  const categoryName =
    typeof product.category === "object"
      ? product.category.name || "Products"
      : categoryMap[String(product.category)]?.name ||
        product.categoryName ||
        "Products";

  const basePrice = getSellingPrice(product);
  const baseMrp = getStrikePrice(product);
  const baseWeightGrams = parseWeightToGrams(product.weight || product.netwt);

  let weightOptions = [];
  if (baseWeightGrams > 0) {
    const optionsInGrams = [250, 500, 1000].filter((g) => g <= baseWeightGrams);
    weightOptions = optionsInGrams.map((grams) => {
      const ratio = grams / baseWeightGrams;
      return {
        weight: formatWeight(grams),
        price: Number((basePrice * ratio).toFixed(2)),
        mrp: Number((baseMrp * ratio).toFixed(2)),
      };
    });
  }

  const defaultOption =
    weightOptions.find((o) => o.weight === "250g") || weightOptions[0] || null;

  return {
    ...product,
    weightOptions,
    img: normalizeImage(product.mainImage || product.image || product.img),
    categoryName,
    price: defaultOption ? defaultOption.price : basePrice,
    old_price: defaultOption ? defaultOption.mrp : baseMrp,
    quantity: 1,
    net_wt: defaultOption ? defaultOption.weight : product.weight || "Units",
    weights: weightOptions.length
      ? weightOptions.map((o) => o.weight)
      : [product.weight || "Units"],
  };
};

const buildCategoryMap = (categories = []) =>
  categories.reduce((acc, category) => {
    acc[String(category._id)] = category;
    return acc;
  }, {});

const searchProducts = (products, query) => {
  const q = query.toLowerCase().trim();
  if (!q) return products;
  return products.filter(
    (p) =>
      p.name?.toLowerCase().includes(q) ||
      p.categoryName?.toLowerCase().includes(q) ||
      p.description?.toLowerCase().includes(q),
  );
};

export default function SearchResults() {
  const { query } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useAppContext();

  // Handle URL decoded query for the input state
  const decodedQuery = useMemo(
    () => (query ? query.replace(/-/g, " ") : ""),
    [query],
  );

  const [searchText, setSearchText] = useState(decodedQuery);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("relevance");

  useEffect(() => {
    setSearchText(decodedQuery);
  }, [decodedQuery]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const [prodRes, catRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/content/products`),
          fetch(`${API_BASE_URL}/api/content/categories`),
        ]);

        const prodJson = await prodRes.json();
        const catJson = await catRes.json();

        const categories = Array.isArray(catJson?.data)
          ? catJson.data
          : catJson || [];
        const catMap = buildCategoryMap(categories);

        const products = Array.isArray(prodJson?.data)
          ? prodJson.data
          : prodJson || [];
        const prepared = products
          .filter(
            (item) =>
              !item.isDeleted && ["Published", "Public"].includes(item.status),
          )
          .map((p) => prepareProduct(p, catMap));

        setAllProducts(prepared);
      } catch (error) {
        console.error("Search results fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const results = useMemo(() => {
    let rows = searchProducts(allProducts, decodedQuery);

    if (sortBy === "price-low") {
      rows = [...rows].sort((a, b) => a.price - b.price);
    } else if (sortBy === "price-high") {
      rows = [...rows].sort((a, b) => b.price - a.price);
    } else if (sortBy === "name") {
      rows = [...rows].sort((a, b) =>
        (a.name || "").localeCompare(b.name || ""),
      );
    }

    return rows;
  }, [allProducts, decodedQuery, sortBy]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const value = searchText.trim();
    if (value) {
      // Navigate to path-based URL /search/:query
      navigate(`/search/${value.replace(/\s+/g, "-")}`);
    } else {
      navigate("/categories");
    }
  };

  const handleQuantityChange = (itemKey, listSetter, delta) => {
    listSetter((prevList) =>
      prevList.map((item) =>
        getItemKey(item) === itemKey
          ? { ...item, quantity: Math.max(1, (item.quantity || 1) + delta) }
          : item,
      ),
    );
  };

  const handleWeightChange = (itemKey, listSetter, newWeight) => {
    listSetter((prevList) =>
      prevList.map((item) => {
        if (getItemKey(item) === itemKey) {
          const selected = item.weightOptions?.find(
            (o) => o.weight === newWeight,
          );
          return {
            ...item,
            net_wt: newWeight,
            price: selected?.price ?? item.price,
            old_price: selected?.mrp ?? item.old_price,
          };
        }
        return item;
      }),
    );
  };

  const handleAddToCart = (product, redirect = false) => {
    const newItem = {
      productId: product.slug || product._id,
      slug: product.slug,
      name: product.name,
      img: product.img,
      categoryName: product.categoryName,
      price: product.price,
      old_price: product.old_price,
      quantity: product.quantity || 1,
      net_wt: product.net_wt,
      weights: product.weights,
    };
    addToCart(newItem, redirect);
  };

  return (
    <>
      <Navbar />

      <main className="bg-light">
        {/* Header Section */}
        <section
          className="pt-100 pb-60"
          style={{ background: "var(--clr-custom-1)" }}
        >
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-8 text-center">
                <div className="bd-section__title-wrapper mb-30">
                  <h2 className="bd-section__title">
                    Search <span>Results</span>
                  </h2>
                </div>

                <div className="breadcrumb__list mb-40 justify-content-center d-flex gap-2 align-items-center">
                  <span>
                    <Link to="/">Home</Link>
                  </span>
                  <span className="dvdr">
                    <i className="fa-regular fa-angle-right"></i>
                  </span>
                  <span>Search</span>
                </div>

                {/* Search Bar */}
                <div className="d-flex justify-content-center">
                  <form
                    onSubmit={handleSearchSubmit}
                    className="bd-action__filter p-relative shadow-sm"
                    style={{
                      maxWidth: "600px",
                      width: "100%",
                      background: "#fff",
                    }}
                  >
                    <input
                      type="text"
                      placeholder="Search for sweets, snacks, bakery..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                    <button className="search-btn" type="submit">
                      <i className="fa-regular fa-magnifying-glass"></i>
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="bd-product__area pt-60 pb-100">
          <div className="container">
            {/* Toolbar */}
            <div className="bd-filter__tab-inner mb-40 bg-white shadow-sm rounded-3 px-4 py-3 d-flex justify-content-between align-items-center flex-wrap">
              <p className="m-0 text-muted">
                Showing <strong>{results.length}</strong> items for{" "}
                <span className="text-dark fw-bold">
                  "{decodedQuery || "all products"}"
                </span>
              </p>

              <div className="d-flex align-items-center mt-2 mt-md-0">
                <label className="me-2 small fw-bold text-uppercase text-muted">
                  Sort By:
                </label>
                <select
                  className="form-select border-0 bg-light rounded-pill px-3 py-2 fw-bold"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  style={{ width: "auto", cursor: "pointer", fontSize: "14px" }}
                >
                  <option value="relevance">Relevance</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="name">Product Name</option>
                </select>
              </div>
            </div>

            {/* CONDITIONAL RENDERING */}
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-danger" role="status"></div>
                <p className="mt-3 text-muted">Preparing your results...</p>
              </div>
            ) : results.length === 0 ? (
              <div className="text-center py-5 bg-white rounded-4 shadow-sm border">
                <i className="fal fa-search-minus fa-4x text-muted mb-3 opacity-50"></i>
                <h3 className="fw-bold">No results found</h3>
                <p className="text-muted">
                  We couldn't find any products matching your search terms.
                </p>
                <Link to="/categories" className="bd-fill__btn mt-3">
                  <span>Explore Catalog</span>
                </Link>
              </div>
            ) : (
              <div className="row g-4">
                {results.map((product) => (
                  <div className="col-xl-3 col-lg-4 col-md-6" key={product._id}>
                    <DynamicDetailedProductCard
                      product={product}
                      listSetter={setAllProducts}
                      onAddToCart={handleAddToCart}
                      onQuantityChange={handleQuantityChange}
                      onWeightChange={handleWeightChange}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
